import React, { useContext, useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import { AuthContext } from "../context/AuthContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import { markRecentGoogleSheet } from "../utils/googleSheetRecents";
import ToolInfoModal from "../components/common/ToolInfoModal";
import LinesheetSourceSelector from "./shared/LinesheetSourceSelector";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import useJobsPagination from "./shared/useJobsPagination";


const STATUS_DONE = "done";
const STATUS_FAILED = "failed";

const ENDPOINT = "/product/sku-builder-laces";

function SkuBuilderLacesTool() {
  const [linesheetSource, setLinesheetSource] = useState("google");
  const [inputFile, setInputFile] = useState(null);
  const [selectedGoogleSheet, setSelectedGoogleSheet] = useState(null);
  const [sheetName, setSheetName] = useState("Laces");
  const [rowsToProcess, setRowsToProcess] = useState("");
  const [includeOnMap, setIncludeOnMap] = useState(false);
  const [customTags, setCustomTags] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const { addToast } = useToast();
  const { user } = useContext(AuthContext);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: `${ENDPOINT}/jobs`,
    cacheKey: "skuBuilderLaces.jobs",
    pageSize: 10,
  });

  const toolInfo = {
    toolName: "SKU Builder (Laces)",
    description:
      'Build shoelace SKUs from the canonical Laces Linesheet. Option1 is Length (e.g. 45") and is intentionally kept out of the product Title. Runs as a background job.',
    steps: [
      "Download the Laces Linesheet template (link on the right). Header is on row 4, sheet tab is 'Laces'.",
      "Fill it in — one row per parent lace product. Multi-length laces (e.g. Keen): list lengths comma-separated in the Length column (e.g. '40, 45, 50, 55').",
      'Length Unit defaults to " (inch). Use "cm" for ECCO Waxed-style products.',
      "Single-variant laces (OS / one-size): leave Length blank. The builder will emit an Option1='Title' / 'Default Title' variant.",
      'Hickory-style one-length-per-product: bake the length into Model Name (e.g. \'Boot Lace 45"\') and leave the Length column blank.',
      "Upload the file. When the job finishes, download the Product Sheet and import via Matrixify.",
    ],
    templates: [
      {
        name: "Laces Linesheet Template",
        link: `${flaskApi.defaults.baseURL}/templates/download/template_laces_linesheet.xlsx`,
      },
    ],
  };

  const guideTips = [
    'Length values are stored with the inch mark — Option1 Value comes out as \'45"\', not "45in".',
    "Length never appears in the auto-generated Title — title = 'Vendor Model (Gender) - Color'. If you want length in the title (Hickory pattern), put it in Model Name.",
    "Variant SKU = '<parent_sku> <length>' (e.g. '1022126 45\"'). For OS / single-variant, the variant SKU is just the parent.",
    "Product Type 'Accessories - Shoe Laces' (plural) maps to Avalara PC040144 automatically.",
    "Output sheet is the same 26-column Matrixify Product Sheet as the other SKU builders — drop-in compatible with Matrixify.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prevStatus = previous[j.job_id];
      if (prevStatus && prevStatus !== j.status) {
        if (j.status === STATUS_DONE) {
          addToast(`Lace SKU build ${j.job_id} complete. Product Sheet is ready.`, "success");
        } else if (j.status === STATUS_FAILED) {
          addToast(`Lace SKU build ${j.job_id} failed: ${j.error || "unknown error"}`, "error");
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(jobs.map((j) => [j.job_id, j.status]));
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const hasLinesheet =
      linesheetSource === "google"
        ? Boolean(selectedGoogleSheet?.id)
        : Boolean(inputFile);

    if (!hasLinesheet) {
      addToast(
        linesheetSource === "google"
          ? "Please choose a shared Google Sheet."
          : "Please upload the laces linesheet.",
        "error"
      );
      return;
    }

    const trimmedSheet = sheetName.trim();
    if (!trimmedSheet) {
      addToast("Please enter the sheet (tab) name.", "error");
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (linesheetSource === "upload" && inputFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }

    const formData = new FormData();
    if (linesheetSource === "google") {
      formData.append("google_linesheet_file_id", selectedGoogleSheet.id);
      formData.append("google_linesheet_file_name", selectedGoogleSheet.name);
    } else {
      formData.append("input_file", inputFile);
    }
    formData.append("sheet_name", trimmedSheet);
    if (rowsToProcess.trim()) formData.append("rows_to_process", rowsToProcess.trim());
    if (includeOnMap) formData.append("include_on_map", "true");
    if (customTags.trim()) formData.append("custom_tags", customTags);

    setUploading(true);
    setUploadProgress(0);

    try {
      const response = await flaskApi.post(ENDPOINT, formData, {
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setUploadProgress(pct);
        },
      });

      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      addToast(`Lace SKU build ${jobId} queued. Tracking progress…`, "info");
      if (page !== 1) await setPage(1);
      else await refetchJobs();

      if (linesheetSource === "google" && selectedGoogleSheet?.id) {
        markRecentGoogleSheet(user?.employeeId, selectedGoogleSheet);
      }
      if (linesheetSource === "upload") {
        setInputFile(null);
      }
    } catch (error) {
      console.error(error);
      const message = await extractErrorMessage(error, "Failed to start lace SKU build.");
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(`${ENDPOINT}/jobs/${job.job_id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.result_filename || "sku_builder_laces.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Product Sheet downloaded.", "success");
    } catch (error) {
      console.error(error);
      const message = await extractErrorMessage(error, "Failed to download Product Sheet.");
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="SKU Builder (Laces)"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form tool-form-split">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Builder Settings</h3>
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Sheet (Tab) Name</label>
                <input
                  type="text"
                  className="tool-input"
                  value={sheetName}
                  onChange={(event) => setSheetName(event.target.value)}
                  placeholder="Laces"
                  required
                  disabled={uploading}
                />
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Rows to Process (Optional)</label>
                <input
                  type="text"
                  className="tool-input"
                  value={rowsToProcess}
                  onChange={(event) => setRowsToProcess(event.target.value)}
                  placeholder="e.g. 6-10, 12, 14 (Leave empty for all)"
                  disabled={uploading}
                />
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Tag Options</label>
                <div className="shopify-checkbox-row">
                  <label className="shopify-checkbox-label">
                    <input
                      type="checkbox"
                      checked={includeOnMap}
                      onChange={(event) => setIncludeOnMap(event.target.checked)}
                      disabled={uploading}
                    />
                    Add on-map tag
                  </label>
                </div>
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Custom Tags (Optional)</label>
                <textarea
                  className="tool-input"
                  value={customTags}
                  onChange={(event) => setCustomTags(event.target.value)}
                  placeholder="e.g. preorder, staff-pick"
                  disabled={uploading}
                />
                <p className="tool-helper-text">
                  Comma- or line-separated tags added to every generated row.
                </p>
              </div>
            </section>

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={uploading}>
                {uploading ? "Uploading…" : "Start Build"}
              </button>
              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading linesheet…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>

          <div className="tool-form-side">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Linesheet Source</h3>
              </div>
              <LinesheetSourceSelector
                source={linesheetSource}
                onSourceChange={setLinesheetSource}
                uploadFile={inputFile}
                onUploadFileChange={setInputFile}
                selectedGoogleSheet={selectedGoogleSheet}
                onSelectedGoogleSheetChange={setSelectedGoogleSheet}
                uploadLabel="Upload Laces Linesheet (Max 100MB)"
              />
            </section>
          </div>
        </form>

        <ToolJobsSection
          title="Your Lace SKU Builds"
          emptyText="No builds yet. Upload a linesheet above to start one."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
                const progress = j.progress || {};
                const total = Number(progress.total) || 0;
                const processed = Number(progress.processed) || 0;
                const variants = Number(progress.variants) || 0;
                const stage = progress.stage || "";
                const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
                return (
                  <div key={j.job_id} className="tool-job-status">
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Job ID</span>
                      <span className="tool-job-value">{j.job_id}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Sheet</span>
                      <span className="tool-job-value">{j.sheet_name || "—"}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Input</span>
                      <span className="tool-job-value">{j.input_filename || "—"}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Status</span>
                      <span className={`tool-job-value tool-job-status-${j.status}`}>
                        {j.status}{stage ? ` (${stage})` : ""}
                      </span>
                    </div>
                    {total > 0 ? (
                      <>
                        <div className="tool-job-status-row">
                          <span className="tool-job-label">Progress</span>
                          <span className="tool-job-value">
                            {processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)
                          </span>
                        </div>
                        <div className="progress-bar-container">
                          <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                      </>
                    ) : null}
                    {variants > 0 ? (
                      <div className="tool-job-counts">
                        <span>Variants: {variants.toLocaleString()}</span>
                      </div>
                    ) : null}
                    {j.status === STATUS_FAILED && j.error ? (
                      <div className="tool-job-error">Error: {j.error}</div>
                    ) : null}
                    {j.status === STATUS_DONE ? (
                      <button
                        type="button"
                        className="tool-btn-primary"
                        onClick={() => handleDownload(j)}
                        disabled={downloadingJobId === j.job_id}
                      >
                        {downloadingJobId === j.job_id ? "Downloading…" : "Download Product Sheet"}
                      </button>
                    ) : null}
                  </div>
                );
              }}
            />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default SkuBuilderLacesTool;
